"use client";

import {
  getSessionTokenFrontendDomain,
  getSupertokensAppName,
  getWebsiteDomain,
} from "@/config/app.config";
import EmailPassword from "supertokens-auth-react/recipe/emailpassword";
import { EmailPasswordPreBuiltUI } from "supertokens-auth-react/recipe/emailpassword/prebuiltui";
import EmailVerification from "supertokens-auth-react/recipe/emailverification";
import Session from "supertokens-auth-react/recipe/session";
import type {
  SessionClaimValidator,
  SuperTokensConfig as SuperTokensFrontendConfig,
} from "supertokens-auth-react/lib/build/types";
import {
  isSecondFactorPending,
  SECOND_FACTOR_CLAIM_ID,
} from "@workspace/core/auth/second-factor";
import {
  buildLoginPath,
  buildSecondFactorPath,
  LOGIN_PATH,
  REFRESH_SESSION_PATH,
} from "@/lib/auth/redirect";
import { invalidateSessionIdentity } from "@/lib/auth/session-identity";

const appName = getSupertokensAppName();
const sessionTokenFrontendDomain = getSessionTokenFrontendDomain();
const EMAIL_VERIFICATION_CLAIM_MAX_AGE_SECONDS = 10;

/**
 * Segundo fator (claim `geo-2fa`). Telas protegidas por `SessionAuth` levam a
 * sessao pendente direto para a tela do codigo, como ja acontece com a
 * verificacao de e-mail. So o backend muda o valor — ao conferir o codigo —,
 * entao nao ha o que buscar aqui.
 */
const secondFactorValidator: SessionClaimValidator = {
  id: SECOND_FACTOR_CLAIM_ID,
  refresh: async () => {},
  shouldRefresh: () => false,
  validate: (accessTokenPayload) =>
    isSecondFactorPending(accessTokenPayload)
      ? { isValid: false, reason: "SECOND_FACTOR_REQUIRED" }
      : { isValid: true },
  onFailureRedirection: () =>
    buildSecondFactorPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    ),
};

export const SuperTokensConfig = {
  appInfo: {
    appName,
    // Usa o mesmo domínio do frontend e proxy /api para evitar third-party cookies no Safari.
    apiDomain: getWebsiteDomain(),
    websiteDomain: getWebsiteDomain(),
    apiBasePath: "/api/auth",
    websiteBasePath: "/auth",
  },

  getRedirectionURL: async (context) => {
    if (context.action === "TO_AUTH") {
      return LOGIN_PATH;
    }

    return undefined;
  },

  recipeList: [
    EmailPassword.init(),
    EmailVerification.init({
      mode: "REQUIRED",
      disableDefaultUI: true,
      getRedirectionURL: async (context) => {
        if (context.action === "VERIFY_EMAIL") {
          return "/auth/verify-email";
        }

        return undefined;
      },
    }),
    Session.init({
      // Força uso de cookies ao invés de headers para tokens
      tokenTransferMethod: "cookie",
      // Permite compartilhar cookies entre subdomínios
      ...(sessionTokenFrontendDomain && { sessionTokenFrontendDomain }),
      override: {
        functions: (originalImplementation) => ({
          ...originalImplementation,
          getGlobalClaimValidators: (input) => {
            const validators =
              originalImplementation.getGlobalClaimValidators(input);

            return [
              ...validators.filter(
                (validator) =>
                  validator.id !== EmailVerification.EmailVerificationClaim.id,
              ),
              EmailVerification.EmailVerificationClaim.validators.isVerified(
                EMAIL_VERIFICATION_CLAIM_MAX_AGE_SECONDS,
                EMAIL_VERIFICATION_CLAIM_MAX_AGE_SECONDS,
              ),
              secondFactorValidator,
            ];
          },
        }),
      },
      onHandleEvent: (event) => {
        if (event.action === "SIGN_OUT") {
          invalidateSessionIdentity(true);
          return;
        }
        if (event.action === "UNAUTHORISED" && event.sessionExpiredOrRevoked) {
          invalidateSessionIdentity(true);
        }
        if (
          event.action !== "UNAUTHORISED" ||
          !event.sessionExpiredOrRevoked ||
          event.userContext?.suppressAuthRedirect === true ||
          window.location.pathname === LOGIN_PATH ||
          // A pagina de refresh decide sozinha, preservando o `redirectTo`.
          window.location.pathname === REFRESH_SESSION_PATH
        ) {
          return;
        }

        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(buildLoginPath(currentPath));
      },
    }),
  ],
} satisfies SuperTokensFrontendConfig;

export const PreBuiltUIList = [EmailPasswordPreBuiltUI];
